import {ChangeDetectionStrategy, Component, HostBinding, Input, Optional} from '@angular/core';
import {ControlValueAccessor, NgModel, ValidationErrors} from '@angular/forms';

@Component({
  selector: 'ds-checkbox',
  templateUrl: './checkbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxComponent implements ControlValueAccessor {
  static checkboxCount = 0;
  @HostBinding('class.ds-checkbox') true;

  @Input() disabled = false;
  @Input() hideLabel = false;
  @Input() name: string;
  @Input() id = `checkbox-${CheckboxComponent.checkboxCount}`;
  /** Object that contains an error message and an Icon class for error */
  @Input() error = {message: '', iconClass: ''};
  /** When showErrorMessages is set to false, showError() will always return false */
  @Input() showErrorMessages = true;
  public errors: ValidationErrors = {};
  onTouchedCallback: () => {};
  onChangeCallback: any;

  get checked() {
    return this._checked;
  }

  /**
   * Updating the state of a checkbox to match the state of the parameter passed in.
   */
  @Input() set checked(checked: boolean) {
    if (checked !== this.checked) {
      this._checked = checked;
      this.triggerOnChangeCallback(checked);
    }
  }

  _checked = null;

  constructor(@Optional() public control: NgModel) {
    CheckboxComponent.checkboxCount++;
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  public toggle() {
    this.checked = !this.checked;
    this.triggerOnChangeCallback(this.checked);
  }

  /**
   * Passes the checkbox value to the onChangeCallback.
   * Sends null if the checkbox is not checked so the required check is triggered.
   *
   * @param checked value of the checkbox
   */
  triggerOnChangeCallback(checked: boolean) {
    if (this.onChangeCallback) {
      this.onChangeCallback(checked ? true : null);
    }
  }

  /**
   * Reacts on [(ngModel)] changes.
   *
   * @param value value passed into [(ngModel)]
   */
  public writeValue(value: any) {
    this.checked = !!value;
  }

  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  onClick() {
    if (!this.disabled) {
      this.toggle();
      if (this.control) {
        this.errors = this.control.errors;
      }
    }
  }

  /**
   * Decides if this input field is valid according to validation attributes.
   *
   * @returns false if the content of this input is valid otherwise true.
   */
  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  /**
   * Decides if an error message should be shown for this textarea field.
   *
   * @returns true if an error message should be shown.
   */
  public get showError(): boolean {
    if (!this.control || !this.showErrorMessages) {
      return false;
    }
    return this.invalid;
  }
}
